import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { GgAddModules } from "../../../pages/clienti/feature/addOn/dto";
import { createAddOn, updateAddOn } from "../../../pages/clienti/feature/addOn/slice";
import { setCustomerCreateRequestField, setCustomerUpdateRequestField } from "../../../pages/clienti/feature/slice";
import { addonSelect } from "../../../utils";
import { colors } from "../../colors";
import { CloseIcon } from "../../icons/close";
import { ButtonComponent } from "../button/newButton";
import { Calendar } from "../calendar";
import { MadfarmInput } from "../input/newInput";
import { MadfarmStaticSelect } from "../select/newSelect/static";

interface Props {
  closeHandler: () => void;
  onEdit?: boolean;
  idx?: number;
  id?: number;
  visible?: boolean;
  mode?: "create" | "edit";
  customerId?: string | number;
}

export function AddOnPopup(props: Props) {
  const { closeHandler, onEdit, visible, idx, mode, id, customerId } = props;

  const dispatch = useAppDispatch();
  const customerState = useAppSelector(state => state.customer);
  const clienteEditCreateRequest =
    mode === "create"
      ? customerState.customerCreateRequest
      : customerState.customerUpdateRequest;

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [module, setModule] = useState<string | undefined>('');
  const [notes, setNotes] = useState<string | undefined>('');

  useEffect(() => {
    if (visible && idx !== undefined && clienteEditCreateRequest.ggAddOns?.[idx]) {
      const addon = clienteEditCreateRequest.ggAddOns[idx];
      setStartDate(addon.starting ? new Date(addon.starting) : undefined);
      setEndDate(addon.ending ? new Date(addon.ending) : undefined);
      setModule(addon.addModule);
      setNotes(addon.addOn);
    } else if (visible && !onEdit) {
      setStartDate(undefined);
      setEndDate(undefined);
      setModule('');
      setNotes('');
    }
  }, [visible, idx, clienteEditCreateRequest.ggAddOns, onEdit]);

  const onSaveHandler = () => {
    if (!module) return;

    const newAddon = {
      starting: startDate as Date,
      ending: endDate,
      addModule: module,
      addOn: notes || '',
    };

    const currentAddOns = clienteEditCreateRequest.ggAddOns
      ? [...clienteEditCreateRequest.ggAddOns]
      : [];

    if (onEdit && idx !== undefined) {
      currentAddOns[idx] = { ...currentAddOns[idx], ...newAddon };
    } else {
      currentAddOns.push(newAddon);
    }

    if (mode === "create") {
      dispatch(setCustomerCreateRequestField({ field: "ggAddOns", value: currentAddOns }));
    } else {
      dispatch(setCustomerUpdateRequestField({ field: "ggAddOns", value: currentAddOns }));

      if (onEdit && id !== undefined) {
        dispatch(updateAddOn({
          id: String(id),
          data: {
            starting: startDate ?? null,
            ending: endDate ?? null,
            addOn: notes || '',
            addModule: module as GgAddModules,
            active: true,
          },
        }));
      } else if (customerId) {
        dispatch(createAddOn({
          starting: startDate ?? null,
          ending: endDate ?? null,
          addOn: notes || '',
          addModule: module as GgAddModules,
          customerDataId: String(customerId),
          active: true,
        }));
      }
    }

    closeHandler();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeHandler}
      />
      <div
        className="relative z-10 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ width: '800px', maxWidth: '95vw', maxHeight: '90vh' }}
      >
        <div className="flex items-center justify-between px-6 py-4 bg-brandPrimary-600">
          <h2 style={{ fontFamily: 'Manrope' }} className="text-white font-semibold text-base">
            {onEdit ? "Modifica add-on" : "Aggiungi add-on"}
          </h2>
          <button
            onClick={closeHandler}
            className="text-white/70 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
          >
            <CloseIcon size={16} color={colors.white} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-neutral-50 px-6 py-5 flex flex-col gap-5">
          <MadfarmStaticSelect
            label="Modulo"
            menuPortalTargetIsNotBody
            defaultValue={module}
            value={module}
            onChange={(value) => {
              //@ts-ignore
              const newValue = value !== null ? value.value : null;
              setModule(newValue);
            }}
            options={addonSelect}
            placeholder="Modulo"
            noOptionsMessage={""}
          />
          <div className="flex items-center justify-between w-full gap-4">
            <div className="w-[calc(50%-8px)]">
              <Calendar label="Dal" selected={startDate} onChange={(date) => date && setStartDate(date)} />
            </div>
            <div className="w-[calc(50%-8px)]">
              <Calendar label="Al" selected={endDate} onChange={(date) => date && setEndDate(date)} />
            </div>
          </div>
          <MadfarmInput
            label="Note"
            defaultValue={notes}
            value={notes}
            onChangeText={setNotes}
            placeholder="Note"
          />
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-white">
          <ButtonComponent
            onClick={closeHandler}
            label="Annulla"
            size="md"
            variant="outline"
            iconPosition="off"
            color="gray"
          />
          <ButtonComponent
            onClick={onSaveHandler}
            label={onEdit ? "Aggiorna" : "Salva"}
            size="md"
            variant="solid"
            iconPosition="off"
            color="orange"
          />
        </div>
      </div>
    </div>
  );
}